import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { artistListState } from "../atom/atom";
import { addNewSong } from "../api/write.api";
import { extractYoutubeInfo } from "../api/common.api";
import { Genre } from "../types/type";
import { generateSongId } from "../api/fetch.api";

interface INewSong {
  title: string;
  youtubeLink: string;
  youtubeId: string;
  thumbnailUrl: string;
  tagList: string[];
  isAI: boolean;
  makerUserId: string;
  tag: {
    genreList: string[];
    [key: string]: any;
  };
}

function AddSongYoutubePage() {
  const [artistId, setArtistId] = useState<string>("");
  const [artistName, setArtistName] = useState<string>("");
  const [newSongList, setNewSongList] = useState<INewSong[]>([
    {
      title: "",
      youtubeLink: "",
      youtubeId: "",
      thumbnailUrl: "",
      tagList: [],
      isAI: true,
      makerUserId: "Homebrew",
      tag: { genreList: [] },
    },
  ]);
  const [tagList, setTagList] = useState<string[]>([]);
  const [genreList, setGenreList] = useState<string[]>([]);
  const artistList = useRecoilValue(artistListState);

  const handleArtistSelect = (artistId: string, artistName: string) => {
    setArtistId(artistId);
    setArtistName(artistName);
    setNewSongList((prevSongList) =>
      prevSongList.map((song) => ({
        ...song,
        tagList: [artistName],
      }))
    );
  };

  const handleYoutubeLinkChange = (index: number, link: string) => {
    const { youtubeId, thumbnailUrl } = extractYoutubeInfo(link);
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) =>
        i === index
          ? { ...song, youtubeLink: link, youtubeId, thumbnailUrl }
          : song
      )
    );
  };

  const handleToggleGenre = (index: number, genre: string) => {
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) => {
        if (i === index) {
          const genreIndex = song.tag.genreList.indexOf(genre);
          if (genreIndex === -1) {
            return {
              ...song,
              tag: {
                ...song.tag,
                genreList: [...song.tag.genreList, genre],
              },
            };
          } else {
            return {
              ...song,
              tag: {
                ...song.tag,
                genreList: song.tag.genreList.filter((g) => g !== genre),
              },
            };
          }
        }
        return song;
      })
    );
    setGenreList((prevList) =>
      prevList.includes(genre)
        ? prevList.filter((g) => g !== genre)
        : [...prevList, genre]
    );
  };

  const handleInputChange = (
    index: number,
    field: keyof INewSong,
    value: string | boolean
  ) => {
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) =>
        i === index ? { ...song, [field]: value } : song
      )
    );
  };

  const handleAddTag = (index: number, tag: string) => {
    setNewSongList((prevSongs) =>
      prevSongs.map((song, i) =>
        i === index
          ? {
              ...song,
              tag: { ...song.tag, tagList: [...song.tag.genreList, tag] },
            }
          : song
      )
    );
    setTagList((prevList) =>
      prevList.includes(tag)
        ? prevList.filter((t) => t !== tag)
        : [...prevList, tag]
    );
  };

  const handleAddRow = () => {
    setNewSongList((prevSongs) => [
      ...prevSongs,
      {
        title: "",
        youtubeLink: "",
        youtubeId: "",
        thumbnailUrl: "",
        tagList: artistName ? [artistName] : [],
        isAI: false,
        makerUserId: "Homebrew",
        tag: { genreList: [], tagList: [] },
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setNewSongList((prevSongs) => prevSongs.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const test = async () => {
      const newSongId = await generateSongId();
      console.log(newSongId);
    };
    test();
  }, []);

  const handleAddSong = async () => {
    try {
      await Promise.all(
        newSongList.map((song) =>
          addNewSong({
            artistId,
            artistName,
            ...song,
          })
        )
      );
      // 폼 초기화
      setArtistId("");
      setArtistName("");
      setNewSongList([
        {
          title: "",
          youtubeLink: "",
          youtubeId: "",
          thumbnailUrl: "",
          tagList: [],
          isAI: newSongList[0].isAI,
          makerUserId: "Homebrew",
          tag: { genreList: [], tagList: [...tagList] },
        },
      ]);
    } catch (error) {
      console.error("Error adding songs: ", error);
      alert("Failed to add songs.");
    }
  };

  return (
    <div>
      <h1>Add New Songs</h1>
      <div>
        <label>Select Artist:</label>
        {artistList.map((artist) => (
          <button
            key={artist.artistId}
            onClick={() =>
              handleArtistSelect(artist.artistId, artist.artistName)
            }
            style={{
              backgroundColor:
                artist.artistId === artistId ? "lightblue" : "white",
              borderColor: artist.artistId === artistId ? "blue" : "grey",
              borderWidth: 0.1,
              marginRight: 4,
            }}
          >
            {artist.artistName}
          </button>
        ))}
      </div>
      {newSongList.map((song, index) => (
        <div
          key={index}
          style={{ marginTop: "20px", borderBottom: "1px solid #ccc" }}
        >
          <div>
            <label>Title:</label>
            <input
              type="text"
              value={song.title}
              onChange={(e) =>
                handleInputChange(index, "title", e.target.value)
              }
            />
          </div>
          <div>
            <label>YouTube Link:</label>
            <input
              type="text"
              value={song.youtubeLink}
              onChange={(e) => handleYoutubeLinkChange(index, e.target.value)}
            />
          </div>
          <div>
            <label>Thumbnail URL:</label>
            <input type="text" value={song.thumbnailUrl} readOnly />
          </div>
          <div>
            <label>Tags (Press Enter to add):</label>
            <input
              type="text"
              defaultValue={"homebrewRecommend"}
              onKeyPress={(e) => {
                if (e.key === "Enter" && e.currentTarget.value) {
                  handleAddTag(index, e.currentTarget.value);
                  e.currentTarget.value = "";
                }
              }}
            />
            <div>
              {song.tagList.map((tag, tagIndex) => (
                <span key={tagIndex} style={{ marginRight: "5px" }}>
                  {tag}
                </span>
              ))}
            </div>
            <div>
              <label>Genre</label>
              {Object.values(Genre).map((genre) => (
                <button
                  key={genre}
                  onClick={() => handleToggleGenre(index, genre)}
                  style={{
                    backgroundColor: song.tag.genreList.includes(genre)
                      ? "lightgreen"
                      : "white",
                    borderColor: song.tag.genreList.includes(genre)
                      ? "green"
                      : "grey",
                    borderWidth: 0.1,
                    marginRight: 4,
                  }}
                >
                  {genre}
                </button>
              ))}
              <div>
                {song.tag.genreList.map((genre, genreIndex) => (
                  <span key={genreIndex} style={{ marginRight: "5px" }}>
                    {genre}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label>Is AI:</label>
            <input
              type="checkbox"
              checked={song.isAI}
              onChange={() => handleInputChange(index, "isAI", !song.isAI)}
            />
          </div>
          <div>
            <label>Maker User ID:</label>
            <input
              type="text"
              value={song.makerUserId}
              onChange={(e) =>
                handleInputChange(index, "makerUserId", e.target.value)
              }
            />
          </div>
          <button type="button" onClick={() => handleRemoveRow(index)}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" onClick={handleAddRow}>
        row 추가
      </button>
      <button type="button" onClick={handleAddSong}>
        데이터 전송
      </button>
    </div>
  );
}

export default AddSongYoutubePage;
